<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Column from 'primevue/column'
import DataTable from '@lib/components/shared/DataTable.vue'
import type { TicketsetSum } from '@/composables/useStatisticsData'

defineProps<{
    rows: TicketsetSum[]
    total: TicketsetSum | null
}>()

const { t } = useI18n()

const expandedId = ref<number | null>(null)

const expandedRows = computed<Record<string, boolean>>(() => {
    if (expandedId.value === null) return {}
    return { [String(expandedId.value)]: true }
})

function onRowClick(row: TicketsetSum) {
    expandedId.value = expandedId.value === row.id ? null : row.id
}

function peopleCell(r: TicketsetSum): string {
    if (r.real !== r.realNodevice) {
        return `${r.real} (${r.realNodevice})`
    }
    return String(r.real)
}
</script>

<template>
    <div class="bg-white border border-gray-200 rounded-12 overflow-hidden mb-4">
        <div class="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-ink">
            {{ t('statistics.table.title') }}
        </div>
        <div class="overflow-x-auto">
        <div class="min-w-[640px]">
        <DataTable
            :value="rows"
            row-key="id"
            :expanded-rows="expandedRows"
            @row-click="onRowClick"
        >
            <Column :header="t('statistics.table.cols.id')" :style="{ width: '60px' }">
                <template #body="{ data: row }">
                    <span class="text-xs tabular-nums text-gray-500">{{ row.id }}</span>
                </template>
            </Column>
            <Column :header="t('statistics.table.cols.event')">
                <template #body="{ data: row }">
                    <span class="text-sm">{{ row.eventName }}</span>
                </template>
            </Column>
            <Column :header="t('statistics.table.cols.name')">
                <template #body="{ data: row }">
                    <span class="text-sm font-medium text-ink">{{ row.name }}</span>
                </template>
            </Column>
            <Column :header="t('statistics.table.cols.toCheck')" :style="{ width: '110px' }">
                <template #body="{ data: row }">
                    <span class="text-sm tabular-nums">{{ row.total }}</span>
                </template>
            </Column>
            <Column :header="t('statistics.table.cols.tickets')" :style="{ width: '110px' }">
                <template #body="{ data: row }">
                    <span class="text-sm tabular-nums">{{ row.used }}</span>
                </template>
            </Column>
            <Column :header="t('statistics.table.cols.people')" :style="{ width: '110px' }">
                <template #body="{ data: row }">
                    <span class="text-sm tabular-nums">{{ peopleCell(row) }}</span>
                </template>
            </Column>
            <template #expansion="{ data: row }">
                <div class="bg-gray-50 px-4 py-3">
                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {{ t('statistics.table.sub.title') }}
                    </div>
                    <div v-if="row.sub.length === 0" class="text-xs text-gray-400 py-2">
                        {{ t('statistics.table.sub.empty') }}
                    </div>
                    <table v-else class="w-full text-xs">
                        <thead>
                            <tr class="border-b border-gray-200 text-gray-500">
                                <th class="px-3 py-1.5 text-left font-semibold">{{ t('statistics.table.sub.cols.type') }}</th>
                                <th class="px-3 py-1.5 text-left font-semibold">{{ t('statistics.table.sub.cols.name') }}</th>
                                <th class="px-3 py-1.5 text-right font-semibold">{{ t('statistics.table.sub.cols.count') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="s in row.sub" :key="s.id" class="border-b border-gray-200/70">
                                <td class="px-3 py-1.5 text-gray-600">{{ s.type }}</td>
                                <td class="px-3 py-1.5">{{ s.name }}</td>
                                <td class="px-3 py-1.5 text-right tabular-nums">{{ s.count }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </DataTable>
        <div
            v-if="total"
            class="grid grid-cols-[60px_1fr_1fr_110px_110px_110px] bg-gray-50 border-t border-gray-200 px-4 py-2.5 text-sm font-semibold text-ink"
        >
            <span></span>
            <span></span>
            <span>{{ t('statistics.table.total') }}</span>
            <span class="tabular-nums">{{ total.total }}</span>
            <span class="tabular-nums">{{ total.used }}</span>
            <span class="tabular-nums">{{ total.real }}</span>
        </div>
        </div>
        </div>
    </div>
</template>
